import React, { useState, useEffect } from 'react';
import { Form, Input, Button, message, notification, Typography, Divider, Space } from 'antd';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import SEO from '../components/SEO';
import { UserOutlined, LockOutlined, ArrowRightOutlined } from '@ant-design/icons';
import api from '../api/axios';
import AuthLayout from '../layouts/AuthLayout';

const { Title, Text } = Typography;

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [emailSubmitted, setEmailSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { redirectTo, selectedPlan } = location.state || {};

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const error = params.get('error');
        if (error) {
            notification.error({
                message: 'Login Error',
                description: decodeURIComponent(error),
            });
            // Clear the error param from URL
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, [location]);

    const handleEmailSubmit = async () => {
        setLoading(true);
        try {
            const response = await api.post('/check-email', { email });
            if (response.status === 200 && response.data.status === 'success') {
                setEmailSubmitted(true);
                message.success('Email verified — please enter your password.');
            } else {
                notification.error({ message: 'Email Verification Failed', description: 'Please try again.' });
            }
        } catch (error) {
            notification.error({ message: 'Email Verification Error', description: 'An error occurred. Please try again later.' });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const response = await api.post('/login', values);
            if (response.status === 200 && response.data.status === 'success') {
                if (response.data.data.mfa_enabled) {
                    navigate('/multi-factor-authentication', {
                        replace: true,
                        state: {
                            mfa_token: response.data.data.mfa_token,
                            mfa_type: response.data.data.mfa_type,
                            redirectTo,
                            selectedPlan,
                        },
                    });
                } else {
                    notification.success({ message: 'Login Successful', description: 'Welcome back!' });
                    localStorage.setItem('user_id', response.data.data.user.id);
                    localStorage.setItem('first_name', response.data.data.user.first_name);
                    localStorage.setItem('last_name', response.data.data.user.last_name);
                    navigate(redirectTo || '/dashboard', { replace: true });
                }
            } else {
                notification.error({ message: 'Login Failed', description: 'Please try again.' });
            }
        } catch (error) {
            if (error.response?.status === 422) {
                const errors = error.response.data.errors;
                Object.keys(errors).forEach((key) =>
                    notification.error({ message: 'Validation Error', description: errors[key][0] })
                );
            } else if (error.response?.status === 401) {
                const errors = error.response.data.errors;
                Object.keys(errors).forEach((key) =>
                    notification.error({ message: 'Login Failed', description: errors[key] })
                );
            } else {
                notification.error({ message: 'Login Error', description: 'An error occurred. Please try again later.' });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <SEO
                title="Login to Your Account"
                description="Sign in to Oottan to connect with your matches, send interests, and find your perfect life partner."
                canonical="/login"
            />
            <AuthLayout>
                <div style={{ width: '100%' }}>
                    <div style={{ textAlign: 'center', marginBottom: '36px' }}>
                        <Title level={2} style={{ color: '#1A1A2E', marginBottom: '6px', fontWeight: '800', fontSize: '1.8rem' }}>
                            {emailSubmitted ? 'Enter Password' : 'Sign In'}
                        </Title>
                        <Text style={{ color: '#64748B', fontSize: '0.95rem' }}>
                            {emailSubmitted ? `Signing in as ${email}` : 'Welcome back to Oottan'}
                        </Text>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '28px' }}>
                        {[0, 1].map((i) => (
                            <div key={i} style={{
                                height: '6px',
                                width: (i === 0 && !emailSubmitted) || (i === 1 && emailSubmitted) ? '24px' : '8px',
                                borderRadius: '9999px',
                                background: (i === 0 && !emailSubmitted) || (i === 1 && emailSubmitted) ? 'linear-gradient(135deg,#AD1457,#D81B60)' : 'rgba(173,20,87,0.2)',
                                transition: 'all 0.3s ease',
                            }} />
                        ))}
                    </div>

                    <Form name="login" initialValues={{ remember: true }} onFinish={emailSubmitted ? handleSubmit : handleEmailSubmit} layout="vertical" size="large">
                        <Form.Item name="email" rules={[{ required: true, message: 'Please input your email!' }]}>
                            <Input prefix={<UserOutlined style={{ color: '#AD1457' }} />} type="email" placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={emailSubmitted} style={{ borderRadius: '10px', height: '50px' }} />
                        </Form.Item>

                        {emailSubmitted && (
                            <Form.Item name="password" rules={[{ required: true, message: 'Please input your password!' }]}>
                                <Input.Password prefix={<LockOutlined style={{ color: '#AD1457' }} />} placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} style={{ borderRadius: '10px', height: '50px' }} />
                            </Form.Item>
                        )}

                        <Form.Item style={{ marginBottom: '12px' }}>
                            <Button type="primary" htmlType="submit" block loading={loading} icon={<ArrowRightOutlined />} style={{ height: '50px', borderRadius: '10px', fontSize: '1rem', fontWeight: '700', background: 'linear-gradient(135deg,#AD1457,#D81B60)', border: 'none', boxShadow: '0 6px 20px rgba(173,20,87,0.35)' }}>
                                {emailSubmitted ? 'Login' : 'Continue'}
                            </Button>
                        </Form.Item>

                        {emailSubmitted && (
                            <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                                <Text style={{ color: '#AD1457', fontSize: '0.88rem', cursor: 'pointer', fontWeight: '500' }} onClick={() => { setEmailSubmitted(false); setPassword(''); }}>
                                    ← Use a different email
                                </Text>
                            </div>
                        )}

                        {emailSubmitted && (
                            <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                                <Link to="/forgot-password" style={{ color: '#94A3B8', fontSize: '0.85rem', textDecoration: 'none' }}>Forgot password?</Link>
                            </div>
                        )}
                    </Form>

                    <Divider style={{ margin: '20px 0', borderColor: '#E2E8F0' }}>
                        <Text style={{ color: '#94A3B8', fontSize: '0.82rem' }}>OR</Text>
                    </Divider>

                    <Space direction="vertical" style={{ width: '100%', marginBottom: '24px' }} size="middle">
                        <Button 
                            block 
                            style={{ 
                                height: '52px', 
                                borderRadius: '12px', 
                                fontWeight: '600',
                                border: '1px solid #E2E8F0',
                                fontSize: '0.95rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                            }}
                            onClick={() => window.location.href = `${process.env.REACT_APP_API_URL}/auth/google/redirect`}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', width: '220px' }}>
                                <div style={{ width: '24px', height: '24px', flexShrink: 0, background: `url(${process.env.REACT_APP_IMAGE_URL}/assets/images/Google.svg) center/contain no-repeat` }} />
                                <span style={{ marginLeft: '12px' }}>Continue with Google</span>
                            </div>
                        </Button>
                        <Button 
                            block 
                            style={{ 
                                height: '52px', 
                                borderRadius: '12px', 
                                fontWeight: '600',
                                border: '1px solid #E2E8F0',
                                fontSize: '0.95rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                            }}
                            onClick={() => window.location.href = `${process.env.REACT_APP_API_URL}/auth/facebook/redirect`}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', width: '220px' }}>
                                <div style={{ width: '24px', height: '24px', flexShrink: 0, background: `url(${process.env.REACT_APP_IMAGE_URL}/assets/images/Facebook.svg) center/contain no-repeat` }} />
                                <span style={{ marginLeft: '12px' }}>Continue with Facebook</span>
                            </div>
                        </Button>
                        <Button 
                            block 
                            style={{ 
                                height: '52px', 
                                borderRadius: '12px', 
                                fontWeight: '600',
                                border: '1px solid #E2E8F0',
                                fontSize: '0.95rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                            }}
                            onClick={() => window.location.href = `${process.env.REACT_APP_API_URL}/auth/zoho/redirect`}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', width: '220px' }}>
                                <div style={{ width: '24px', height: '24px', flexShrink: 0, background: `url(${process.env.REACT_APP_IMAGE_URL}/assets/images/Zoho.svg) center/contain no-repeat` }} />
                                <span style={{ marginLeft: '12px' }}>Continue with Zoho</span>
                            </div>
                        </Button>
                    </Space>

                    <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                        <Text style={{ color: '#64748B', fontSize: '0.9rem' }}>Don't have an account?{' '}</Text>
                        <Link to="/signup" style={{ color: '#AD1457', fontWeight: '700', fontSize: '0.9rem', textDecoration: 'none' }}>Create one free</Link>
                    </div>

                    <div style={{ textAlign: 'center', paddingTop: '16px', borderTop: '1px solid #F1F5F9' }}>
                        <Text style={{ color: '#94A3B8', fontSize: '0.78rem' }}>
                            {process.env.REACT_APP_RELEASE_DATE} · v{process.env.REACT_APP_RELEASE_VERSION}
                        </Text>
                    </div>
                </div>
            </AuthLayout>
        </>
    );
};

export default Login;
